import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, DollarSign, TrendingUp, Receipt, Clock } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { API } from "@/App";
import { cn } from "@/lib/utils";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-4">
      <div className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] text-slate-500 font-mono mb-2">
        <Icon className={cn("h-3 w-3", accent || "text-emerald-400")} />
        {label}
      </div>
      <div className="text-2xl font-bold text-white tabular-nums">{value}</div>
    </div>
  );
}

export default function RevenueTab({ registerRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await axios.get(`${API}/admin/metrics/revenue`, {
        headers: { Authorization: `Bearer ${session?.access_token || ""}` },
      });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load revenue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (registerRefresh) registerRefresh(() => fetchData);
  }, [registerRefresh, fetchData]);

  if (loading) {
    return (
      <div className="py-20 text-center text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin inline" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="hq-tab-revenue">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={DollarSign} label="Total Revenue (USD)" value={`$${data.total_revenue_usd.toFixed(2)}`} />
        <Stat icon={TrendingUp} label="Paid Sessions" value={data.paid_count} />
        <Stat icon={Clock} label="Pending" value={data.pending_count} accent="text-amber-400" />
        <Stat icon={Receipt} label="Packages" value={Object.keys(data.by_package).length} accent="text-sky-400" />
      </div>

      {Object.keys(data.by_package).length > 0 && (
        <div className="rounded-lg border border-slate-800 bg-slate-900/50 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-800 text-[0.65rem] uppercase tracking-[0.2em] text-slate-500 font-mono">
            Revenue by Package
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-900/80 border-b border-slate-800">
              <tr className="text-left text-[0.65rem] uppercase tracking-[0.18em] text-slate-500">
                <th className="px-4 py-2 font-semibold">Package</th>
                <th className="px-4 py-2 font-semibold text-right">Count</th>
                <th className="px-4 py-2 font-semibold text-right">Total (USD)</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.by_package).map(([pkg, info]) => (
                <tr key={pkg} className="border-b border-slate-800 last:border-0">
                  <td className="px-4 py-2 text-xs text-slate-200 font-mono">{pkg}</td>
                  <td className="px-4 py-2 text-xs text-right text-slate-300 tabular-nums">{info.count}</td>
                  <td className="px-4 py-2 text-xs text-right text-emerald-300 tabular-nums">${info.total.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-lg border border-slate-800 bg-slate-900/50 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-800 text-[0.65rem] uppercase tracking-[0.2em] text-slate-500 font-mono">
          Recent Transactions (last 25)
        </div>
        {data.recent_transactions.length === 0 ? (
          <div className="px-4 py-8 text-center text-xs text-slate-600 italic">
            No transactions yet. Once chefs start upgrading to Pro, they&apos;ll show up here.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-900/80 border-b border-slate-800">
              <tr className="text-left text-[0.65rem] uppercase tracking-[0.18em] text-slate-500">
                <th className="px-4 py-2 font-semibold">When</th>
                <th className="px-4 py-2 font-semibold">User</th>
                <th className="px-4 py-2 font-semibold">Package</th>
                <th className="px-4 py-2 font-semibold">Status</th>
                <th className="px-4 py-2 font-semibold text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_transactions.map((t, i) => (
                <tr key={i} className="border-b border-slate-800 last:border-0">
                  <td className="px-4 py-2 text-[0.7rem] text-slate-400 font-mono">
                    {t.created_at ? new Date(t.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-2 text-[0.7rem] text-slate-400 font-mono">
                    {t.user_id ? t.user_id.slice(0, 8) + "…" : "—"}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-300">{t.package_id || "—"}</td>
                  <td className="px-4 py-2">
                    <span className={cn(
                      "text-[0.65rem] px-2 py-0.5 rounded-full font-semibold",
                      t.status === "paid" ? "bg-emerald-500/15 text-emerald-300"
                        : t.status === "pending" ? "bg-amber-500/15 text-amber-300"
                        : "bg-slate-700 text-slate-300"
                    )}>
                      {t.status || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-right tabular-nums text-white">
                    {(t.currency || "USD").toUpperCase() === "USD" ? "$" : ""}{t.amount.toFixed(2)} {(t.currency || "").toUpperCase()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
